// Browser notification utilities

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications')
    return 'denied'
  }

  if (Notification.permission === 'granted') {
    return 'granted'
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission()
    return permission
  }

  return Notification.permission
}

export function showBrowserNotification(
  title: string,
  options?: NotificationOptions
): Notification | null {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications')
    return null
  }

  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted')
    return null
  }

  const notification = new Notification(title, {
    icon: '/icon-192.png',
    badge: '/icon-192.png',
    ...options,
  })

  return notification
}

export function scheduleTaskReminder(
  taskTitle: string,
  dueDate: Date,
  reminderMinutes: number,
  onClick?: () => void
): number | null {
  const now = new Date()
  const reminderTime = new Date(dueDate.getTime() - reminderMinutes * 60 * 1000)

  if (reminderTime <= now) {
    return null // Don't schedule reminders in the past
  }

  const timeUntilReminder = reminderTime.getTime() - now.getTime()

  const timeoutId = window.setTimeout(() => {
    const notification = showBrowserNotification(
      `Upcoming Task: ${taskTitle}`,
      {
        body: `Due in ${reminderMinutes} minutes`,
        tag: `task-reminder-${taskTitle}`,
        requireInteraction: false,
      }
    )

    if (notification && onClick) {
      notification.onclick = onClick
    }
  }, timeUntilReminder)

  return timeoutId
}

export function cancelScheduledReminder(timeoutId: number): void {
  window.clearTimeout(timeoutId)
}

export function showAchievementNotification(
  achievementName: string,
  achievementIcon: string,
  points: number
): Notification | null {
  return showBrowserNotification(
    `Achievement Unlocked! ${achievementIcon}`,
    {
      body: `${achievementName} - You earned ${points} points!`,
      tag: 'achievement-unlock',
      requireInteraction: true,
    }
  )
}

export function showLowStockNotification(
  supplyName: string,
  currentQuantity: number
): Notification | null {
  return showBrowserNotification(
    'Low Stock Alert',
    {
      body: `${supplyName} is running low (${currentQuantity} remaining)`,
      tag: 'low-stock',
      requireInteraction: false,
    }
  )
}

export function showDailySummary(
  tasksToday: number,
  completed: number,
  pending: number
): Notification | null {
  return showBrowserNotification(
    'Daily Task Summary',
    {
      body: `Today: ${completed}/${tasksToday} tasks completed, ${pending} pending`,
      tag: 'daily-summary',
      requireInteraction: false,
    }
  )
}

export function showOverdueTaskNotification(
  taskTitle: string
): Notification | null {
  return showBrowserNotification(
    'Overdue Task',
    {
      body: `"${taskTitle}" is overdue`,
      tag: 'task-overdue',
      requireInteraction: true,
    }
  )
}
